/** Repère (s, t) du fuseau utilisateur (« Manœuvre libre », registre K2 ; docs/14-manoeuvre-libre.md §5.1).
 *
 *  Portage exact de `generex_engine.user_corridor.Frame` et de `reprojection.transport` : mêmes stations,
 *  même interpolation bilinéaire par quadrilatère dans un plan équirectangulaire local, même inverse (forme
 *  close, repli de Newton, ordre des candidats, image la plus proche), vérifiés par le jeu de vecteurs partagé
 *  `__fixtures__/frame.json` (0,5 m). Le serveur fait foi : ce module sert à l'aperçu pendant les gestes.
 *
 *  Points en [lon, lat] (ordre GeoJSON, celui du modèle stocké) ; le moteur travaille en (lat, lon), l'ordre
 *  des opérations est gardé pour obtenir les mêmes flottants. */

import type { LonLat } from "../geo";

export type { LonLat };

/** Rayon du plan local et des distances (CoordinateCalculationHelper de l'iPad). */
export const R_M = 6_378_137;

// Inverse du repère (frame.json « algorithm ») : forme close sur chaque quadrilatère ; Newton en repli.
const NEWTON_MAX_ITER = 8;
const NEWTON_STEP = 1e-12;
const LOCATE_EPS = 1e-6;
const LOCATE_RESIDUAL_M = 1e-3; // antécédent admis s'il retombe à moins d'1 mm du point
const NEAREST_SAMPLES = 8; // point sans antécédent : barreaux échantillonnés par quadrilatère (u = k/8)
const NEAREST_EXTRA = 12; // et, aux deux bouts, barreaux extrapolés jusqu'à u = −4 095 / 4 096
const NEAREST_ITER = 40; // puis section dorée autour du meilleur

/** `terrain.helper_distance_m` : haversine sur 6 378 137 m (ordre des opérations du serveur). */
export function helperDistance(a: LonLat, b: LonLat): number {
  const lat1 = (a[1] * Math.PI) / 180;
  const lon1 = (a[0] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const lon2 = (b[0] * Math.PI) / 180;
  const dlat = lat2 - lat1;
  const dlon = lon2 - lon1;
  const h =
    Math.sin(dlat / 2) * Math.sin(dlat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlon / 2) * Math.sin(dlon / 2);
  return R_M * (2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)));
}

/** Plan équirectangulaire local centré sur le barycentre (moyenne lat/lon) des points. */
export interface Plane {
  lat0: number;
  lon0: number;
  /** Mètres par degré de longitude. */
  mx: number;
  /** Mètres par degré de latitude. */
  my: number;
}

export function planeOf(points: readonly LonLat[]): Plane {
  let lat = 0;
  let lon = 0;
  for (const p of points) {
    lat += p[1];
    lon += p[0];
  }
  const lat0 = lat / points.length;
  const lon0 = lon / points.length;
  const k = Math.PI / 180;
  return { lat0, lon0, mx: R_M * Math.cos(lat0 * k) * k, my: R_M * k };
}

export function toLocal(p: LonLat, plane: Plane): [number, number] {
  return [(p[0] - plane.lon0) * plane.mx, (p[1] - plane.lat0) * plane.my];
}

export function fromLocal(x: number, y: number, plane: Plane): LonLat {
  return [plane.lon0 + x / plane.mx, plane.lat0 + y / plane.my];
}

/** `bisect.bisect_right` : rang d'insertion après les valeurs égales. */
function bisectRight(values: readonly number[], x: number): number {
  let lo = 0;
  let hi = values.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (x < values[mid]!) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

/** `math.copysign(x, sign)` (le signe de −0 compte). */
function copysign(x: number, sign: number): number {
  const negative = sign < 0 || Object.is(sign, -0);
  return negative ? -Math.abs(x) : Math.abs(x);
}

type Quad = readonly [LonLat, LonLat, LonLat, LonLat];
type Solution = readonly [u: number, v: number, exact: boolean];

/** Repère (s, t) d'un fuseau apparié : `left` (t = −1) et `right` (t = +1), en [lon, lat].
 *
 *  - stations : longueur curviligne cumulée des milieux des paires (haversine, 6 378 137 m), sur [0, 1] ;
 *  - point(s, t) : quadrilatère i tel que stations[i] ≤ s (le plus grand, borné à [0, n − 2]),
 *    u = (s − sᵢ)/(sᵢ₊₁ − sᵢ), v = (t + 1)/2, interpolation bilinéaire dans le plan local du quadrilatère,
 *    extrapolée hors de [0, 1] ;
 *  - locate(p) : inverse en forme close (repli : Newton), quadrilatères du plus proche au plus éloigné ;
 *    `point(...locate(p))` retombe sur p à moins d'1 cm dans l'image du repère, sinon image la plus proche. */
export class CorridorFrame {
  readonly left: readonly LonLat[];
  readonly right: readonly LonLat[];
  readonly centers: readonly LonLat[];
  readonly stations: readonly number[];
  /** Longueur de l'axe médian (m). */
  readonly axisLength: number;

  constructor(left: readonly LonLat[], right: readonly LonLat[]) {
    if (left.length !== right.length || left.length < 2)
      throw new Error("Repère du fuseau : il faut au moins deux paires de sommets.");
    this.left = left.map((p) => [Number(p[0]), Number(p[1])] as LonLat);
    this.right = right.map((p) => [Number(p[0]), Number(p[1])] as LonLat);
    this.centers = this.left.map((g, i) => {
      const d = this.right[i]!;
      return [(g[0] + d[0]) / 2, (g[1] + d[1]) / 2] as LonLat;
    });
    const cumulative = [0];
    for (let i = 0; i + 1 < this.centers.length; i++)
      cumulative.push(cumulative[i]! + helperDistance(this.centers[i]!, this.centers[i + 1]!));
    const total = cumulative[cumulative.length - 1]!;
    const n = this.centers.length;
    this.stations = total > 0 ? cumulative.map((x) => x / total) : this.centers.map((_, i) => i / (n - 1));
    this.axisLength = total;
  }

  get size(): number {
    return this.left.length;
  }

  /** Même géométrie (égalité du serveur : `left` et `right` seulement). */
  equals(other: CorridorFrame): boolean {
    if (other === this) return true;
    if (other.size !== this.size) return false;
    const same = (a: readonly LonLat[], b: readonly LonLat[]) => a.every((p, i) => p[0] === b[i]![0] && p[1] === b[i]![1]);
    return same(this.left, other.left) && same(this.right, other.right);
  }

  private quad(i: number): Quad {
    return [this.left[i]!, this.right[i]!, this.left[i + 1]!, this.right[i + 1]!];
  }

  quadIndex(s: number): number {
    return Math.min(Math.max(bisectRight(this.stations, s) - 1, 0), this.size - 2);
  }

  point(s: number, t: number): LonLat {
    const i = this.quadIndex(s);
    const span = this.stations[i + 1]! - this.stations[i]!;
    const u = span > 0 ? (s - this.stations[i]!) / span : 0;
    const v = (t + 1) / 2;
    const quad = this.quad(i);
    const plane = planeOf(quad);
    const [g0x, g0y] = toLocal(quad[0], plane);
    const [d0x, d0y] = toLocal(quad[1], plane);
    const [g1x, g1y] = toLocal(quad[2], plane);
    const [d1x, d1y] = toLocal(quad[3], plane);
    const x = (1 - u) * ((1 - v) * g0x + v * d0x) + u * ((1 - v) * g1x + v * d1x);
    const y = (1 - u) * ((1 - v) * g0y + v * d0y) + u * ((1 - v) * g1y + v * d1y);
    return fromLocal(x, y, plane);
  }

  /** Antécédents (u, v) du point par l'interpolation bilinéaire du quadrilatère i, en forme close :
   *  P = G₀ + u·e + v·f + u·v·g, d'où k₂·v² + k₁·v + k₀ = 0 (k₂ = g∧f, k₁ = e∧f + h∧g, k₀ = h∧e) ; racines par
   *  la formule stable, u par la composante au plus grand dénominateur. Sans antécédent réel : Newton depuis
   *  (0,5 ; 0,5). Chaque antécédent porte `exact` (retombe à moins d'1 mm du point). */
  private solve(i: number, p: LonLat): Solution[] {
    const quad = this.quad(i);
    const plane = planeOf(quad);
    const [g0x, g0y] = toLocal(quad[0], plane);
    const [d0x, d0y] = toLocal(quad[1], plane);
    const [g1x, g1y] = toLocal(quad[2], plane);
    const [d1x, d1y] = toLocal(quad[3], plane);
    const hx = (p[0] - plane.lon0) * plane.mx - g0x;
    const hy = (p[1] - plane.lat0) * plane.my - g0y;
    const ex = g1x - g0x;
    const ey = g1y - g0y;
    const fx = d0x - g0x;
    const fy = d0y - g0y;
    const gx = g0x - g1x + d1x - d0x;
    const gy = g0y - g1y + d1y - d0y;
    const k2 = gx * fy - gy * fx;
    const k1 = ex * fy - ey * fx + hx * gy - hy * gx;
    const k0 = hx * ey - hy * ex;
    const roots: number[] = [];
    const w = k1 * k1 - 4 * k0 * k2;
    if (w >= 0) {
      const q = -0.5 * (k1 + copysign(Math.sqrt(w), k1));
      if (q !== 0) roots.push(k0 / q);
      if (k2 !== 0) roots.push(q / k2);
    }
    const out: Solution[] = [];
    for (const v of roots) {
      const denX = ex + gx * v;
      const denY = ey + gy * v;
      let u: number;
      if (Math.abs(denX) >= Math.abs(denY)) {
        if (denX === 0) continue;
        u = (hx - fx * v) / denX;
      } else {
        u = (hy - fy * v) / denY;
      }
      if (!(Number.isFinite(u) && Number.isFinite(v))) continue;
      // Racine parasite d'un quadrilatère dégénéré (côté de longueur nulle) : écartée.
      const rx = u * ex + v * fx + u * v * gx - hx;
      const ry = u * ey + v * fy + u * v * gy - hy;
      if (Math.hypot(rx, ry) <= LOCATE_RESIDUAL_M) out.push([u, v, true]);
    }
    if (out.length) return out.sort((a, b) => Math.abs(a[1] - 0.5) - Math.abs(b[1] - 0.5));
    const [u, v] = newton([g0x, g0y], [d0x, d0y], [g1x, g1y], [d1x, d1y], [hx + g0x, hy + g0y]);
    const rx = u * ex + v * fx + u * v * gx - hx;
    const ry = u * ey + v * fy + u * v * gy - hy;
    return [[u, v, Math.hypot(rx, ry) <= LOCATE_RESIDUAL_M]];
  }

  /** Abscisse `s` d'un point d'une limite (t < 0 : gauche, sinon droite) : segment de la limite le plus
   *  proche, fraction du point projeté sur lui dans le plan local de son quadrilatère (`Frame.rail_s`). */
  railS(p: LonLat, t: number): number {
    const rail = t < 0 ? this.left : this.right;
    let best: [number, number, number] = [Infinity, 0, 0];
    for (let i = 0; i < this.size - 1; i++) {
      const plane = planeOf(this.quad(i));
      const [ax, ay] = toLocal(rail[i]!, plane);
      const [bx, by] = toLocal(rail[i + 1]!, plane);
      const [px, py] = toLocal(p, plane);
      const dx = bx - ax;
      const dy = by - ay;
      const length2 = dx * dx + dy * dy;
      const u = length2 > 0 ? Math.min(Math.max(((px - ax) * dx + (py - ay) * dy) / length2, 0), 1) : 0;
      const gap = Math.hypot(px - ax - u * dx, py - ay - u * dy);
      if (gap < best[0] - 1e-9) best = [gap, i, u];
    }
    const [, i, u] = best;
    return this.stations[i]! + u * (this.stations[i + 1]! - this.stations[i]!);
  }

  /** (s, t) d'un point. Antécédents exacts des quadrilatères par distance croissante au barycentre de leurs
   *  sommets (puis |v − 0,5| croissant) : le premier dans le quadrilatère ; à défaut, le premier dont la
   *  tranche en `s` contient le point (ou extrapolé aux deux bouts) ; à défaut, l'image la plus proche. */
  locate(p: LonLat): [number, number] {
    const order: [number, number][] = [];
    for (let i = 0; i < this.size - 1; i++) {
      if (this.stations[i + 1]! - this.stations[i]! <= 0) continue;
      const quad = this.quad(i);
      const centroid: LonLat = [
        (quad[0][0] + quad[1][0] + quad[2][0] + quad[3][0]) / 4,
        (quad[0][1] + quad[1][1] + quad[2][1] + quad[3][1]) / 4,
      ];
      order.push([helperDistance(p, centroid), i]);
    }
    if (!order.length) return [0, 0];
    order.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const last = this.size - 2;
    const lo = -LOCATE_EPS;
    const hi = 1 + LOCATE_EPS;
    const exact: [number, number, number][] = [];
    for (const [, i] of order) for (const [u, v, ok] of this.solve(i, p)) if (ok) exact.push([i, u, v]);
    let chosen = exact.find(([, u, v]) => lo <= u && u <= hi && lo <= v && v <= hi);
    chosen ??= exact.find(([i, u]) => (lo <= u && u <= hi) || (i === 0 && u < lo) || (i === last && u > hi));
    if (!chosen) return this.nearest(p);
    const [i, u, v] = chosen;
    return [this.stations[i]! + u * (this.stations[i + 1]! - this.stations[i]!), 2 * v - 1];
  }

  /** Point sans antécédent : pied de la perpendiculaire au barreau Gᵤ → Dᵤ le plus proche (échantillons
   *  u = k/8, extrapolés aux deux bouts, puis section dorée) ; le premier quadrilatère d'image plus proche de
   *  plus de 1e-9 m l'emporte (`Frame._nearest`). */
  private nearest(p: LonLat): [number, number] {
    let best: [number, number, number] | null = null;
    for (let i = 0; i < this.size - 1; i++) {
      if (this.stations[i + 1]! - this.stations[i]! <= 0) continue;
      const quad = this.quad(i);
      const plane = planeOf(quad);
      const [g0x, g0y] = toLocal(quad[0], plane);
      const [d0x, d0y] = toLocal(quad[1], plane);
      const [g1x, g1y] = toLocal(quad[2], plane);
      const [d1x, d1y] = toLocal(quad[3], plane);
      const [px, py] = toLocal(p, plane);

      /** (distance du point à la droite du barreau u, v du pied de la perpendiculaire). */
      const rung = (u: number): [number, number] => {
        const gx = g0x + u * (g1x - g0x);
        const gy = g0y + u * (g1y - g0y);
        const rx = d0x + u * (d1x - d0x) - gx;
        const ry = d0y + u * (d1y - d0y) - gy;
        const length2 = rx * rx + ry * ry;
        if (length2 <= 0) return [Math.hypot(px - gx, py - gy), 0.5];
        const cross = rx * (py - gy) - ry * (px - gx);
        return [Math.abs(cross) / Math.sqrt(length2), ((px - gx) * rx + (py - gy) * ry) / length2];
      };

      let samples: number[] = [];
      for (let k = 0; k <= NEAREST_SAMPLES; k++) samples.push(k / NEAREST_SAMPLES);
      if (i === 0) {
        const before: number[] = [];
        for (let k = NEAREST_EXTRA; k > 0; k--) before.push(-(2 ** k - 1));
        samples = [...before, ...samples];
      }
      if (i === this.size - 2) for (let k = 1; k <= NEAREST_EXTRA; k++) samples.push(2 ** k);
      let k = 0;
      let kGap = rung(samples[0]!)[0];
      for (let j = 1; j < samples.length; j++) {
        const gap = rung(samples[j]!)[0];
        if (gap < kGap) [k, kGap] = [j, gap];
      }
      let lo = samples[Math.max(k - 1, 0)]!;
      let hi = samples[Math.min(k + 1, samples.length - 1)]!;
      const ratio = (Math.sqrt(5) - 1) / 2;
      for (let n = 0; n < NEAREST_ITER; n++) {
        const x1 = hi - ratio * (hi - lo);
        const x2 = lo + ratio * (hi - lo);
        if (rung(x1)[0] <= rung(x2)[0]) hi = x2;
        else lo = x1;
      }
      const mid = (lo + hi) / 2;
      const u = rung(mid)[0] < rung(samples[k]!)[0] ? mid : samples[k]!;
      const v = rung(u)[1];
      const s = this.stations[i]! + u * (this.stations[i + 1]! - this.stations[i]!);
      const t = 2 * v - 1;
      const gap = helperDistance(this.point(s, t), p);
      if (best === null || gap < best[0] - 1e-9) best = [gap, s, t];
    }
    return best === null ? [0, 0] : [best[1], best[2]];
  }
}

/** Repli : Newton sur l'interpolation bilinéaire, depuis (0,5 ; 0,5). */
function newton(
  g0: [number, number],
  d0: [number, number],
  g1: [number, number],
  d1: [number, number],
  p: [number, number],
): [number, number] {
  const [g0x, g0y] = g0;
  const [d0x, d0y] = d0;
  const [g1x, g1y] = g1;
  const [d1x, d1y] = d1;
  const [px, py] = p;
  let u = 0.5;
  let v = 0.5;
  for (let n = 0; n < NEWTON_MAX_ITER; n++) {
    const x = (1 - u) * ((1 - v) * g0x + v * d0x) + u * ((1 - v) * g1x + v * d1x) - px;
    const y = (1 - u) * ((1 - v) * g0y + v * d0y) + u * ((1 - v) * g1y + v * d1y) - py;
    const jux = (1 - v) * (g1x - g0x) + v * (d1x - d0x);
    const juy = (1 - v) * (g1y - g0y) + v * (d1y - d0y);
    const jvx = (1 - u) * (d0x - g0x) + u * (d1x - g1x);
    const jvy = (1 - u) * (d0y - g0y) + u * (d1y - g1y);
    const det = jux * jvy - juy * jvx;
    if (Math.abs(det) < 1e-9) break;
    const du = (x * jvy - jvx * y) / det;
    const dv = (jux * y - x * juy) / det;
    u -= du;
    v -= dv;
    if (Math.abs(du) + Math.abs(dv) < NEWTON_STEP) break;
  }
  return [u, v];
}

/** Repère d'un fuseau (limites [lon, lat] du modèle stocké). */
export function frameOf(c: { left: readonly LonLat[]; right: readonly LonLat[] }): CorridorFrame {
  return new CorridorFrame(c.left, c.right);
}

/** Point exprimé dans le repère `source`, recalculé dans `target` (registre K5, `reprojection.transport`) ;
 *  identité si les deux repères sont égaux. Le point reçoit le déplacement de son image : st = locate(p),
 *  p + (target.point(st) − source.point(st)), la longitude remise à l'échelle de la latitude du point. Égal à
 *  `target.point(st)` quand locate est exact ; un point sans antécédent suit l'image la plus proche. */
export function transport(source: CorridorFrame, target: CorridorFrame, p: LonLat): LonLat {
  if (source.equals(target)) return p;
  const [s, t] = source.locate(p);
  const a = source.point(s, t);
  const b = target.point(s, t);
  const k = Math.PI / 180;
  const lonScale = Math.cos(a[1] * k) / Math.max(Math.cos(p[1] * k), 1e-12);
  return [p[0] + (b[0] - a[0]) * lonScale, p[1] + (b[1] - a[1])];
}
